// src/pages/SsoLogin.jsx — Admin -> client-app SSO landing page (30 Aug 2026)
//
// Reached only via the Admin Board's "Open Client App" link
// (AdminBoard.jsx), which mints a short-lived, single-use token
// (/api/admin/sso/mint) and opens this route with it in a new tab:
//   /sso?token=<opaque>
//
// This page has NO session yet — that's the whole point — so it must be a
// public route (see App.jsx: listed alongside /login, /team/accept-invite,
// outside MaintenanceGate/ProtectedRoute). It burns the token exactly once
// via /api/auth/sso/exchange (public — the token IS the credential, same
// pattern as team-invite acceptance), which hands back an email +
// Supabase-issued hashed_token. verifyOtp() below turns that into a real
// session, stored the normal way (Supabase's own localStorage handling —
// no manual token plumbing here), then this redirects to /dashboard.
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { exchangeAdminSsoToken } from '../api/kailux'
import { supabase } from '../supabase'
import ErrorMessage from '../components/ErrorMessage'


import { T } from '../theme/theme'
export default function SsoLogin() {
  const [params] = useSearchParams()
  const token = params.get('token') || ''
  const [status, setStatus] = useState('exchanging') // exchanging | error
  const [error, setError] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setError('This sign-in link is missing its token.')
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const { hashed_token } = await exchangeAdminSsoToken(token)
        // FIX (Aug 2026): Supabase's own /verify endpoint rejects this call
        // with 400 "Only the token_hash and type should be provided" if
        // `email` is included alongside `token_hash` — confirmed directly
        // against Supabase's auth logs, not assumed. `email` was leftover
        // from an earlier draft of this flow; token_hash + type alone is
        // enough for GoTrue to resolve the underlying magiclink token.
        const { error: otpError } = await supabase.auth.verifyOtp({
          token_hash: hashed_token, type: 'magiclink',
        })
        if (otpError) throw otpError
        if (cancelled) return
        navigate('/dashboard', { replace: true })
      } catch (err) {
        if (cancelled) return
        setStatus('error')
        setError(
          err.response?.data?.detail ||
          err.message ||
          'This sign-in link has expired or has already been used. Go back to the Admin Board and click "Open Client App" again.'
        )
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  return (
    <div style={{ minHeight:'100vh', background:T.bg, display:'flex',
      alignItems:'center', justifyContent:'center', padding:'20px',
      fontFamily:"'Outfit',sans-serif", color:T.tx }}>
      <div style={{ width:'100%', maxWidth:'380px', background:T.s1,
        border:`1px solid ${T.bd}`, borderRadius:'16px', padding:'32px',
        textAlign:'center' }}>

        <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px',
          fontWeight:'800', marginBottom:'6px' }}>
          Kai<span style={{ color:T.or }}>Lux</span>
        </div>

        {status === 'exchanging' && (
          <div style={{ fontSize:'14px', color:T.mt, marginTop:'14px' }}>
            Signing you in…
          </div>
        )}

        {status === 'error' && (
          <>
            <ErrorMessage style={{ margin:'14px 0', textAlign:'left' }}>{error}</ErrorMessage>
            <Link to="/login" style={{ color:T.or, fontSize:'13px' }}>Go to login</Link>
          </>
        )}
      </div>
    </div>
  )
}
